import React from "react";
import SEO from "@/components/site/SEO";

/**
 * Shared editorial layout for TSR's static legal pages (Kebijakan Privasi,
 * Syarat & Ketentuan). Uses the same Fraunces + Plus Jakarta Sans hierarchy
 * as the Jurnal so the pages feel part of the brand instead of a boilerplate
 * "policy dump".
 *
 * Props:
 *   title       — page H1 (Fraunces via .headline)
 *   subtitle    — one-sentence lede rendered under the title
 *   lastUpdated — ISO-ish string ("13 Agustus 2026") shown as an editorial byline
 *   testid      — root data-testid for the page
 *   seoDesc     — meta description
 *   children    — the actual policy prose (Section components below)
 */
export function LegalPageLayout({ title, subtitle, lastUpdated, testid, seoDesc, children }) {
  const canonical = typeof window !== "undefined" ? window.location.origin + window.location.pathname : undefined;
  return (
    <div data-testid={testid} className="max-w-3xl mx-auto px-6 lg:px-10 py-16">
      <SEO title={`${title} | The School Reports`} description={seoDesc} canonical={canonical} />
      <div className="eyebrow" data-testid={`${testid}-eyebrow`}>Dokumen Publik</div>
      <h1 className="headline mt-3" data-testid={`${testid}-h1`}>{title}</h1>
      {subtitle && <p className="mt-5 text-[17px] leading-relaxed text-[#4B5563]">{subtitle}</p>}
      <p className="mt-4 text-xs text-[#8A8F99] uppercase tracking-widest">
        Terakhir diperbarui · {lastUpdated}
      </p>
      <div className="mt-10 space-y-10 text-[#1F2937]" data-testid={`${testid}-body`}>
        {children}
      </div>
      <div className="mt-16 pt-8 border-t border-[#EAE7E0] text-sm text-[#4B5563]">
        Ada pertanyaan tentang dokumen ini? Kirim email ke{" "}
        <a href="mailto:hello@theschoolreports.id" className="text-[#D2673E] hover:underline">
          hello@theschoolreports.id
        </a>.
      </div>
    </div>
  );
}

/** One section inside a legal page — H2 in Fraunces + prose in Plus Jakarta Sans. */
export function LegalSection({ heading, children, testid }) {
  return (
    <section data-testid={testid} className="scroll-mt-24">
      <h2 className="font-serif-display text-2xl lg:text-[28px] text-[#1a2456] leading-snug">
        {heading}
      </h2>
      <div className="mt-3 space-y-3 text-[15px] leading-[1.72] text-[#1F2937]">
        {children}
      </div>
    </section>
  );
}
